import { ThemeOptions } from '@mui/material';

// Light mode palette constants
export const lightPaletteColors = {
    // Primary colors
    primary: {
        main: '#F17A20',
        hover: '#F38F45',
        contrastText: '#ffffff',
        divider: '#E5E7EB',
    },

    // Secondary colors
    secondary: {
        main: '#7ED321',
        contrastText: '#ffffff',
    },

    // Background colors
    background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
        divider: '#E5E7EB',
    },

    // Text colors
    text: {
        primary: '#2C3E50', // Dark blue-gray for better readability
        secondary: '#5F6B6C', // Medium gray, 5.5:1 on white (WCAG AA)
        disabled: '#BDC1C6',
    },

    // Semantic colors
    success: {
        main: '#28A745',
    },
    warning: {
        main: '#F5A623',
    },
    error: {
        main: '#fc0362',
    },
    info: {
        main: '#03a5fc',
    },

    // Gray scale
    grey: {
        50: '#F8F9FA',
        100: '#F1F3F4',
        200: '#E8EAED',
        300: '#DADCE0',
        400: '#BDC1C6',
        500: '#9AA0A6',
        600: '#80868B',
        700: '#5F6368',
        800: '#3C4043',
        850: '#34495e',
        900: '#202124',
    },
};

// Light mode UI colors
export const lightUiColors = {
    primaryButton: '#4B69FE', // Updated blue from image
    secondaryButton: '#7ED321', // Updated green from image
    accentHover: '#2E5BBA', // Darker blue for hover
    background: '#FFFFFF', // White
    textPrimary: '#2C3E50', // Dark blue-gray for better readability
    textSecondary: '#5F6B6C', // Medium gray, 5.5:1 on white (WCAG AA)
    gradientStart: '#4A90E2', // Blue gradient start
    gradientEnd: '#7ED321', // Green gradient end
    link: '#4A90E2',
    linkHover: '#2E5BBA',
};

// Keep original VMB Education gradient colors for landing page navigation buttons only
export const landingGradientColors = {
    vividGreen: '#7ED321',
    brightBlue: '#4A90E2',
    deepBlue: '#2E5BBA',
    lightSkyBlue: '#87CEEB',
    white: '#FFFFFF',
};

// Extended color palette for light mode
export const lightColors = {
    ...lightUiColors,

    // Semantic colors
    primary: lightPaletteColors.primary.main,
    success: lightPaletteColors.success.main,
    warning: lightPaletteColors.warning.main,
    error: lightPaletteColors.error.main,
    info: lightPaletteColors.info.main,

    // Neutral colors
    grey: lightPaletteColors.grey,

    // Text colors
    text: lightPaletteColors.text,

    gradients: {
        primary: `linear-gradient(135deg, #2563eb 0%, #10b981 100%)`,
        primaryHover: `linear-gradient(135deg, #1d4ed8 0%, #059669 100%)`,
        secondary: lightUiColors.secondaryButton,
        success: lightPaletteColors.success.main,
        warning: lightPaletteColors.warning.main,
        error: lightPaletteColors.error.main,
        info: lightPaletteColors.info.main,
        background: lightPaletteColors.background.default,
        card: lightPaletteColors.background.paper,
    },

    blue: {
        300: '#3498db',
    },

    lightGreen: {
        300: '#1abc9c',
    },

    green: {
        300: '#2ecc71',
    },

    purple: {
        300: '#9b59b6',
    },

    red: {
        200: '#e74c3c',
    },
};

const isVietnamese = (language?: string) => {
    const currentLang =
        language || (typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('i18nextLng') : '');
    return currentLang === 'vi' || currentLang === 'vi-VN';
};

const getFontFamily = (defaultFont: string, vietnameseFont: string, language?: string) => {
    return isVietnamese(language) ? vietnameseFont : defaultFont;
};

export const lightThemeOptions = (language?: string): ThemeOptions => {
    return {
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
                xxl: 1920,
            },
        },

        palette: {
            mode: 'light',
            primary: lightPaletteColors.primary,
            secondary: lightPaletteColors.secondary,
            background: lightPaletteColors.background,
            text: lightPaletteColors.text,
            success: lightPaletteColors.success,
            warning: lightPaletteColors.warning,
            error: lightPaletteColors.error,
            info: lightPaletteColors.info,
            grey: lightPaletteColors.grey,
            divider: lightPaletteColors.primary.divider,
        },

        typography: {
            fontFamily: [
                'IBM Plex Sans',
                'Open Sans',
                'Noto Sans',
                '-apple-system',
                'BlinkMacSystemFont',
                'Segoe UI',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            fontSize: 16,
            h1: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '3.5rem',
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
            },
            h2: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '2.5rem',
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
            },
            h3: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '2.25rem',
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
            },
            h4: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1.875rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            h5: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            h6: {
                fontFamily: getFontFamily('Poppins', 'Open Sans', language),
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
            body1: {
                fontSize: '1.125rem',
                lineHeight: 1.6,
                fontWeight: 400,
            },
            body2: {
                fontSize: '1rem',
                lineHeight: 1.5,
                fontWeight: 400,
            },
            subtitle1: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.25rem',
                lineHeight: 1.5,
                fontWeight: 500,
            },
            subtitle2: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.125rem',
                lineHeight: 1.5,
                fontWeight: 500,
            },
            menu: {
                fontFamily: getFontFamily('Sarabun', 'Noto Sans', language),
                fontSize: '1.125rem',
                lineHeight: 1.2,
                fontWeight: 400,
            },
        },

        shape: {
            borderRadius: 8,
        },

        spacing: 8,

        gradients: {
            primary: lightColors.gradients.primary,
            secondary: lightColors.secondaryButton,
            success: lightColors.success,
            background: lightColors.background,
            card: lightColors.gradients.card,
            button: lightColors.gradients.primary,
            buttonHover: lightColors.gradients.primaryHover,
        },

        landingGradients: {
            primary: `linear-gradient(135deg, ${landingGradientColors.brightBlue} 0%, ${landingGradientColors.vividGreen} 100%)`,
            hover: `linear-gradient(135deg, ${landingGradientColors.deepBlue} 0%, #5CB015 100%)`,
            text: `linear-gradient(135deg, ${lightUiColors.primaryButton} 0%, ${lightUiColors.secondaryButton} 100%)`,
        },

        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 50,
                        fontWeight: 600,
                        fontSize: '1.125rem',
                        padding: '8px 16px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                        },
                    },
                    contained: {
                        background: lightColors.primary,
                    },
                    outlined: {
                        background: '#fff',
                        borderColor: '#fff',
                        color: lightColors.primary,
                        borderWidth: 2,
                    },
                },
                variants: [
                    {
                        props: { variant: 'link' },
                        style: {
                            borderColor: lightUiColors.primaryButton,
                            color: lightUiColors.primaryButton,
                            borderWidth: 2,
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: 'unset',
                                color: lightUiColors.linkHover,
                                backgroundColor: 'transparent',
                                borderWidth: 0,
                                boxShadow: 'none',
                            },
                        },
                    },
                    {
                        props: { variant: 'noOutlined' },
                        style: {
                            borderColor: 'unset',
                            color: lightUiColors.primaryButton,
                            borderWidth: 0,
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: lightUiColors.accentHover,
                                backgroundColor: 'rgba(74, 144, 226, 0.04)',
                                borderWidth: 2,
                            },
                        },
                    },
                ],
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        height: 8,
                        background: lightColors.grey[200],
                    },
                    bar: {
                        borderRadius: 10,
                        background: `linear-gradient(90deg, ${lightUiColors.primaryButton} 0%, ${lightUiColors.secondaryButton} 100%)`,
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    h1: {
                        background: `linear-gradient(135deg, ${lightUiColors.primaryButton} 0%, ${lightUiColors.secondaryButton} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    },
                },
            },
        },
    };
};
